import { FabricObject, Group as FabricGroup, Rect as FabricRect, StaticCanvas, Canvas as FabricCanvas } from 'fabric';

import { Coords } from 'editor/src/store/design/types';

import CustomFabricGroup from 'editor/src/fabric/CustomFabricGroup';
import CustomFabricImage, { isFabricImage } from 'editor/src/fabric/CustomFabricImage';
import CustomFabricPath from 'editor/src/fabric/CustomFabricPath';
import FabricPathText, { isFabricPathText } from 'editor/src/fabric/FabricPathText';

import {
  disposeElement,
  removeCacheCanvas,
  setClipPath,
} from 'editor/src/component/EditorArea/fabricComponents/fabricUtils';
import orderObjects from 'editor/src/component/EditorArea/orderObjects';
import getClipPath from 'editor/src/component/EditorArea/Spread/Page/MediaElement/getClipPath';
import {
  getActiveSelection,
  getActiveSelectionElementOffset,
} from 'editor/src/component/EditorArea/utils/groupSelectionUtils';

export interface BBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

function updateObject(
  objectBase: FabricObject & { zIndex?: number },
  clone: FabricObject & { zIndex?: number },
  mediaBox: BBox,
  ratio: number,
  offset: Coords,
) {
  clone.set({
    angle: objectBase.angle,
    width: objectBase.width,
    height: objectBase.height,
    scaleX: (objectBase.scaleX ?? 1) * ratio,
    scaleY: (objectBase.scaleY ?? 1) * ratio,
    left: ((objectBase.left || 0) - mediaBox.x - offset.left) * ratio,
    top: ((objectBase.top || 0) - mediaBox.y - offset.top) * ratio,
    zIndex: objectBase.zIndex,
  });
}

function updateRectangle(objectBase: FabricGroup, clone: FabricGroup) {
  const cloneChild = clone.getObjects()[0] as FabricRect & { zIndex?: number };
  const objectBaseChild = objectBase.getObjects()[0] as FabricRect & { zIndex?: number };
  const objectBaseChildClipPath = objectBaseChild.clipPath as FabricRect | undefined;

  cloneChild.set({
    clipPath: new FabricRect({
      left: objectBaseChildClipPath?.left || 0,
      top: objectBaseChildClipPath?.top || 0,
      width: objectBaseChildClipPath?.width || 0,
      height: objectBaseChildClipPath?.height || 0,
      rx: objectBaseChildClipPath?.rx,
      ry: objectBaseChildClipPath?.ry,
    }),
    angle: objectBaseChild.angle,
    width: objectBaseChild.width,
    height: objectBaseChild.height,
    left: objectBaseChild.left || 0,
    top: objectBaseChild.top || 0,
    strokeWidth: objectBaseChild.strokeWidth,
    stroke: objectBaseChild.stroke,
    fill: objectBaseChild.fill,
    paintFirst: 'fill',
    rx: objectBaseChild.rx,
    ry: objectBaseChild.ry,
    strokeDashArray: objectBaseChild.strokeDashArray,
    objectCaching: false,
    zIndex: objectBaseChild.zIndex,
  });
}

function updateTextObject(objectBase: FabricPathText, clone: FabricPathText) {
  removeCacheCanvas(clone.clipPath as CustomFabricPath);
  clone.set({
    fill: objectBase.fill,
    text: objectBase.text,
    fontFamily: objectBase.fontFamily,
    textAlign: objectBase.textAlign,
    fontSize: objectBase.fontSize,
    lineHeight: objectBase.lineHeight,
    charSpacing: objectBase.charSpacing,
    shadow: objectBase.shadow,
    paintFirst: 'stroke',
    clipPath: undefined,
    stroke: objectBase.stroke,
    strokeWidth: objectBase.strokeWidth,
    objectCaching: false,
  });
}

function updateImageObject(objectBase: CustomFabricImage, clone: CustomFabricImage, mediaBox: BBox, ratio: number) {
  if (objectBase.frameRect && objectBase.clipPath) {
    const frameRect = {
      left: (objectBase.frameRect.left - mediaBox.x) * ratio,
      top: (objectBase.frameRect.top - mediaBox.y) * ratio,
      width: objectBase.frameRect.width * ratio,
      height: objectBase.frameRect.height * ratio,
      angle: objectBase.frameRect.angle,
    };
    setClipPath(clone, getClipPath(frameRect, [], true));
  } else {
    setClipPath(clone, undefined);
  }

  clone.flipX = objectBase.flipX;
  clone.flipY = objectBase.flipY;

  clone.setElement(objectBase.getElement());
}

function updateMirrorCanvas(
  fabricCanvas: FabricCanvas,
  mirrorCanvas: StaticCanvas,
  mediaBoxBox: BBox,
  ratio: number,
  filteredUids?: Set<number>,
) {
  const mirrorObjects: Map<number, FabricObject> = new Map();
  mirrorCanvas.getObjects().forEach((object) => {
    if ((object as any).uuid) {
      mirrorObjects.set((object as any).uuid, object);
    }
  });
  const mainCanvasObjects = fabricCanvas.getObjects();

  // objects inside the group are positioned differently
  const multiActiveSelection = getActiveSelection(fabricCanvas);
  const activeObjects = fabricCanvas.getActiveObjects();
  const groupOffset = multiActiveSelection
    ? getActiveSelectionElementOffset(multiActiveSelection)
    : { left: 0, top: 0 };

  for (let i = 0; i < mainCanvasObjects.length; i += 1) {
    const mainObject = mainCanvasObjects[i];

    let elementOffset = { left: 0, top: 0 };
    if (multiActiveSelection && activeObjects.includes(mainObject)) {
      elementOffset = groupOffset;
    }

    if (isFabricPathText(mainObject) && mainObject.isFontLoaded) {
      const { uuid } = mainObject;
      if (!uuid) {
        continue;
      }

      if (filteredUids && !filteredUids.has(uuid)) {
        continue;
      }

      const mirrorObject = mirrorObjects.get(uuid) as FabricPathText | undefined;
      const clone = mirrorObject || new FabricPathText(mainObject.text);
      updateObject(mainObject as FabricObject, clone as FabricObject, mediaBoxBox, ratio, elementOffset);
      updateTextObject(mainObject, clone);
      if (!mirrorObject) {
        clone.uuid = mainObject.uuid;
        mirrorCanvas.add(clone);
      }

      if (mainObject.uuid) {
        mirrorObjects.delete(mainObject.uuid);
      }
    } else if (isFabricImage(mainObject) && mainObject.uuid !== undefined && mainObject.isLoaded) {
      const { uuid } = mainObject;
      if (filteredUids && !filteredUids.has(uuid)) {
        continue;
      }
      const mirrorObject = mirrorObjects.get(mainObject.uuid) as CustomFabricImage | undefined;
      const clone = mirrorObject || new CustomFabricImage(new Image());
      updateObject(mainObject, clone, mediaBoxBox, ratio, elementOffset);
      updateImageObject(mainObject, clone, mediaBoxBox, ratio);
      if (!mirrorObject) {
        clone.uuid = mainObject.uuid;
        mirrorCanvas.add(clone);
      }

      mirrorObjects.delete(mainObject.uuid);
    } else if (mainObject instanceof CustomFabricGroup && mainObject.customType === 'gelato-rectangle') {
      const { uuid } = mainObject as any;
      if (filteredUids && !filteredUids.has(uuid)) {
        continue;
      }
      const mirrorObject = mirrorObjects.get(uuid) as FabricGroup | undefined;

      const clone = mirrorObject || new FabricGroup([new FabricRect()]);
      updateObject(mainObject as FabricObject, clone as FabricObject, mediaBoxBox, ratio, elementOffset);
      updateRectangle(mainObject, clone);
      if (!mirrorObject) {
        (clone as any).uuid = uuid;
        mirrorCanvas.add(clone);
      }

      mirrorObjects.delete(uuid);
    }
  }

  const mirrorObjectsToDelete = [...mirrorObjects.values()];
  if (mirrorObjectsToDelete.length) {
    mirrorObjectsToDelete.forEach(disposeElement);
    mirrorCanvas.remove(...mirrorObjectsToDelete);
  }

  orderObjects(mirrorCanvas);
  mirrorCanvas.renderAll();
}

export default updateMirrorCanvas;
